import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { ErrorComponent } from "~/components/error-boundary";
import { getStringWithStaticDataLinks, StaticDataParser } from "~/components/static-data-parser";
import { glossaryData } from "~/config/glossary";
import { getSeoMeta } from "~/utils/seo";

export const sitemap = () => {
  const letters = glossaryData
    .map((item) =>
      item.word[0]
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
    )
    .filter((value, index, self) => {
      return self.indexOf(value) === index;
    });
  return letters.map((letter) => ({
    loc: `/glossaire/${letter}`,
  }));
};

export const meta: MetaFunction = (metaArgs) => {
  const letter = metaArgs.params.letter?.toUpperCase() || "A";
  return getSeoMeta(metaArgs, {
    title: `Glossaire du photovoltaique et de l'énergie solaire, lettre ${letter}`,
    description: `POwR Connect vous propose un lexique sur les notions clés du photovoltaïque et de l'énergie solaire. Voici la lettre ${letter}.`,
    structuredData: {
      "@type": "FAQPage",
      mainEntity: glossaryData.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: getStringWithStaticDataLinks(item.definition),
        },
      })),
    },
  });
};

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const currentLetter = params.letter || "a";
  return json({ currentLetter });
};

const GlossaryLetterPage = () => {
  const { currentLetter } = useLoaderData<typeof loader>();

  return (
    <div>
      <h2 className="mb-8 text-3xl font-medium">{currentLetter.toUpperCase()}</h2>
      {glossaryData
        .filter(
          (item) =>
            currentLetter ===
            item.word[0]
              ?.normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
        )
        .map((item, index) => (
          <GlossaryItem key={index} word={item.word} definition={item.definition} />
        ))}
    </div>
  );
};

export default GlossaryLetterPage;

const GlossaryItem = ({ word, definition }: { word: string; definition: string }) => {
  return (
    <div className="mb-8">
      <h3 className="mb-2 text-lg font-medium">{word}</h3>
      <p className="leading-normal text-grey-600">
        <StaticDataParser string={definition} />
      </p>
    </div>
  );
};

export const ErrorBoundary = () => {
  return <ErrorComponent />;
};
